import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import {
    useGridApiRef,
    DataGridPro,
} from '@mui/x-data-grid-pro';
import {
    Box,
    TextField,
    MenuItem,
    Button
} from '@mui/material';

import {
    listProductsPrice,
    unloadProductsPrice,
} from '../../../modules/productModule/productsPrice';
import * as yup from 'yup';

const ProductPriceListContainer = ({ onSelectedProducts }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [pageSize, setPageSize] = React.useState(50);
    const [selectedRows, setSelectedRows] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState("productNm");
    const [searchWords, setSearchWords] = useState(null);

    const { products, error, loading } = useSelector(
        ({ productModule, loading }) => ({
            products: productModule.productsPrice.products,
            error: productModule.productsPrice.error,
            loading: loading['product/LIST_PRODUCTS_PRICE'],
        }),
    );

    // 컴포넌트 마운트 후 list 데이터 요청
    useEffect(() => {
        dispatch(listProductsPrice());

        return () => {
            dispatch(unloadProductsPrice());
        };
    }, []);

    const searchList = (values) => {
        let searchData = {
            [values.searchKeyword]: values.searchWords,
        };

        setSearchKeyword(values.searchKeyword);
        setSearchWords(values.searchWords);

        dispatch(listProductsPrice(searchData));

    }

    const initialValues = {
        searchKeyword: searchKeyword,
        searchWords: searchWords,
    };


    const validationSchema = yup.object({
        searchKeyword: yup.string('').nullable(),
        searchWords: yup.string('').trim().nullable(),

    });

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);

            setTimeout(() => {
                searchList(values);
                setSubmitting(false);
            }, 1000);
        },
    });


    const apiRef = useGridApiRef();

    const columns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                hide: true,
                type: 'string',
                editable: false,
            },

            {
                field: 'productUuid',
                headerName: '제품코드',
                hide: true,
                type: 'string',
                editable: false,
            },
            {
                field: 'partnerNm',
                headerName: '제조사',
                minWidth: 200,
                flex: 1,
                type: 'string',
                editable: false,
            },
            {
                field: 'pricePartnerNm',
                headerName: '매입처',
                minWidth: 200,
                flex: 1,
                type: 'string',
                editable: false,
            },
            {
                field: 'productNm',
                headerName: '제품명',
                minWidth: 200,
                flex: 1,
                type: 'string',
                editable: false,
            },

            {
                field: 'modelNm',
                headerName: '모델명',
                minWidth: 200,
                flex: 1,
                type: 'string',
                editable: false,
            },

            {
                field: 'priceUuid',
                headerName: '가격ID',
                hide: true,
            },
            {
                field: 'priceTyp',
                headerName: '가격타입',
                minWidth: 150,
                flex: 0.8,
            },
            {
                field: 'priceVal',
                headerName: '매입가',
                minWidth: 150,
                flex: 0.8,
                type: 'number',
                editable: false,
                valueFormatter: (params) => {
                    const valueFormatted = Number(params.value).toLocaleString('ko-KR');
                    return `₩ ${valueFormatted}`;
                },
            },
        ],
        [],
    );


    const handleChecked = () => {
        const rowValues = Array.from(apiRef.current.getSelectedRows().values());
        //console.log(rowValues, 'row');
        onSelectedProducts(rowValues);
    }


    return (
        <Box>
            <Box sx={{ my: 1, display: 'flex', justifyContent: ' space-between', alignItems: 'center' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        my: 1,
                    }}
                >
                    <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            mr: 2,
                        }}
                        onSubmit={formik.handleSubmit}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                '& > :not(style)': { m: 1, width: '25ch' },
                            }}
                        >
                            <TextField
                                variant="standard"
                                required={false}
                                select
                                id="searchKeyword"
                                name="searchKeyword"
                                label="검색키워드"
                                value={formik.values.searchKeyword}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.searchKeyword &&
                                    Boolean(formik.errors.searchKeyword)
                                }
                                helperText={
                                    formik.touched.searchKeyword && formik.errors.searchKeyword
                                }
                            >
                                {/* <MenuItem value="">검색키워드</MenuItem> */}
                                <MenuItem value="productNm">제품명</MenuItem>
                                <MenuItem value="partnerNm">제조사</MenuItem>
                                <MenuItem value="pricePartnerNm">매입처</MenuItem>
                                <MenuItem value="modelNm">모델명</MenuItem>
                            </TextField>
                            <TextField
                                variant="standard"
                                required={false}
                                id="searchWords"
                                name="searchWords"
                                label="검색어"
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.searchWords && Boolean(formik.errors.searchWords)
                                }
                                helperText={
                                    formik.touched.searchWords && formik.errors.searchWords
                                }
                            />
                        </Box>

                        <Box>
                            <Button
                                type="submit"
                                color="info"
                                variant="contained"
                                disabled={formik.isSubmitting}
                            >
                                검색
                            </Button>
                        </Box>
                    </Box>


                </Box>
                <Box><Button variant="outlined" color="secondary" onClick={handleChecked}>제품 선택</Button></Box>
            </Box>
            <div style={{ display: 'flex', height: '300px' }}>
                <div style={{ flexGrow: 1 }}>
                    <DataGridPro
                        apiRef={apiRef}
                        rows={products ? products : []}
                        columns={columns}

                        loading={loading}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        pagination
                        checkboxSelection
                        disableSelectionOnClick
                    />
                </div>
            </div>
        </Box>

    )
}

export default ProductPriceListContainer